export type SessionUser = {
  sub: string;
  email: string | null;
  name: string | null;
  picture: string | null;
  authTime: number | null;
  amr: string[];
  /** パスワードの最終変更日時（ISO 8601）。ログイン時点の値 */
  lastPasswordReset?: string | null;
};

export type SessionState =
  | { status: "authenticated"; user: SessionUser }
  | { status: "unauthenticated"; loginUrl: string }
  | { status: "ip_not_allowed"; ip: string | null }
  | { status: "not_configured" }
  | { status: "error" };

export const LOGIN_URL = "/api/auth/auth0/start";
export const LOGOUT_URL = "/api/auth/auth0/logout";
export const STEP_UP_URL = "/api/auth/auth0/step-up";

/** API のエラー。code はサーバーの error.code（通信できなければ NETWORK_ERROR） */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly body: Record<string, unknown>;
  readonly retryAfterSeconds: number | null;

  constructor(
    status: number,
    code: string,
    body: Record<string, unknown>,
    retryAfter: number | null
  ) {
    super(`${status} ${code}`);
    this.status = status;
    this.code = code;
    this.body = body;
    this.retryAfterSeconds = retryAfter;
  }

  /** 401 UNAUTHENTICATED / REAUTHENTICATION_REQUIRED の遷移先 */
  get loginUrl(): string | null {
    return typeof this.body.loginUrl === "string" ? this.body.loginUrl : null;
  }

  /** 401 STEP_UP_REQUIRED の遷移先 */
  get stepUpUrl(): string | null {
    return typeof this.body.stepUpUrl === "string" ? this.body.stepUpUrl : null;
  }
}

export function isApiError(error: unknown, code?: string): error is ApiError {
  return error instanceof ApiError && (code === undefined || error.code === code);
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      ...init,
      headers: { accept: "application/json", ...init.headers },
    });
  } catch {
    throw new ApiError(0, "NETWORK_ERROR", {}, null);
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let body: unknown = null;
  try {
    body = text === "" ? null : JSON.parse(text);
  } catch {
    body = null;
  }
  if (res.ok) return body as T;
  const error =
    body !== null && typeof body === "object" && "error" in body && typeof body.error === "object"
      ? (body.error as Record<string, unknown>)
      : {};
  const retryAfter = Number(res.headers.get("retry-after"));
  throw new ApiError(
    res.status,
    typeof error.code === "string" ? error.code : `HTTP_${res.status}`,
    error,
    Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : null
  );
}

function jsonBody(method: string, value: unknown): RequestInit {
  return { method, headers: { "content-type": "application/json" }, body: JSON.stringify(value) };
}

export async function fetchSession(): Promise<SessionState> {
  try {
    const body = await request<{ user: SessionUser }>("/api/session");
    return { status: "authenticated", user: body.user };
  } catch (error) {
    if (!isApiError(error)) return { status: "error" };
    if (error.code === "AUTH_NOT_CONFIGURED") return { status: "not_configured" };
    if (error.code === "IP_NOT_ALLOWED") {
      const ip = typeof error.body.ip === "string" ? error.body.ip : null;
      return { status: "ip_not_allowed", ip };
    }
    if (error.status === 401) {
      return { status: "unauthenticated", loginUrl: error.loginUrl ?? LOGIN_URL };
    }
    return { status: "error" };
  }
}

// プロダクト一覧

export type Product = {
  id: string;
  name: string;
  description: string;
  loginUrl: string;
  /** アイコン画像の URL。null なら表示名の頭文字を出す */
  iconUrl: string | null;
};

export type ProductList = { products: Product[]; incomplete: boolean };

export function fetchProducts(): Promise<ProductList> {
  return request("/api/products");
}

// 基本設定

export type Profile = {
  email: string | null;
  displayName: string | null;
  customDisplayName: string | null;
  picture: string | null;
  customPicture: string | null;
};

export const DISPLAY_NAME_MAX_LENGTH = 50;
export const PICTURE_MAX_BYTES = 5 * 1024 * 1024;

export function fetchProfile(): Promise<Profile> {
  return request("/api/profile");
}

export function updateDisplayName(displayName: string | null): Promise<Profile> {
  return request("/api/profile", jsonBody("PATCH", { displayName }));
}

export function uploadPicture(file: Blob): Promise<Profile> {
  return request("/api/profile/picture", {
    method: "PUT",
    headers: { "content-type": file.type },
    body: file,
  });
}

export function deletePicture(): Promise<Profile> {
  return request("/api/profile/picture", { method: "DELETE" });
}

// パスワード

export function requestPasswordChange(): Promise<void> {
  return request("/api/password/change-request", { method: "POST" });
}

// ログイン記録

export type LoginEventKind =
  | "login_succeeded"
  | "login_failed"
  | "logged_out"
  | "mfa_succeeded"
  | "mfa_failed"
  | "mfa_enrolled"
  | "password_changed"
  | "password_change_requested";

export type LoginEvent = {
  id: string | null;
  date: string;
  kind: LoginEventKind;
  eventCode: string;
  product: string | null;
  ip: string | null;
  location: { country: string | null; city: string | null } | null;
  browser: string | null;
  os: string | null;
};

export type LoginHistoryPage = {
  items: LoginEvent[];
  page: number;
  hasMore: boolean;
  retentionDays: number;
};

export function fetchLoginHistory(page: number): Promise<LoginHistoryPage> {
  return request(`/api/login-history?page=${page}`);
}

// 認証手段（My Account API を BFF 経由で呼ぶ）
// https://auth0.com/docs/api/myaccount

export type AuthenticationMethod = {
  id: string;
  type: string;
  created_at: string;
  confirmed?: boolean;
  name?: string;
  phone_number?: string;
  preferred_authentication_method?: "sms" | "voice";
  user_agent?: string;
  credential_device_type?: "single_device" | "multi_device";
  last_auth_at?: string;
};

export async function fetchAuthenticationMethods(): Promise<AuthenticationMethod[]> {
  const body = await request<{ authentication_methods?: AuthenticationMethod[] }>(
    "/api/my-account/authentication-methods"
  );
  return body?.authentication_methods ?? [];
}

export function deleteAuthenticationMethod(id: string): Promise<void> {
  return request(`/api/my-account/authentication-methods/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export type TotpEnrollment = {
  id: string;
  auth_session: string;
  barcode_uri: string;
  manual_input_code?: string;
};

export type PhoneEnrollment = { id: string; auth_session: string };

export type PasskeyEnrollment = {
  id?: string;
  auth_session: string;
  authn_params_public_key: PasskeyCreationOptionsJSON;
};

/** WebAuthn の PublicKeyCredentialCreationOptions の JSON 表現（バイナリは base64url） */
export type PasskeyCreationOptionsJSON = {
  challenge: string;
  rp: { id?: string; name: string };
  user: { id: string; name: string; displayName: string };
  pubKeyCredParams: { type: "public-key"; alg: number }[];
  authenticatorSelection?: AuthenticatorSelectionCriteria;
  timeout?: number;
  excludeCredentials?: { id: string; type: "public-key"; transports?: string[] }[];
};

export function startTotpEnrollment(): Promise<TotpEnrollment> {
  return request("/api/my-account/authentication-methods", jsonBody("POST", { type: "totp" }));
}

export function startPhoneEnrollment(phoneNumber: string): Promise<PhoneEnrollment> {
  return request(
    "/api/my-account/authentication-methods",
    jsonBody("POST", {
      type: "phone",
      phone_number: phoneNumber,
      preferred_authentication_method: "sms",
    })
  );
}

export function startPasskeyEnrollment(): Promise<PasskeyEnrollment> {
  return request("/api/my-account/authentication-methods", jsonBody("POST", { type: "passkey" }));
}

export function verifyOtpEnrollment(id: string, authSession: string, otpCode: string) {
  return request(
    `/api/my-account/authentication-methods/${encodeURIComponent(id)}/verify`,
    jsonBody("POST", { auth_session: authSession, otp_code: otpCode })
  );
}

export function verifyPasskeyEnrollment(id: string, authSession: string, authnResponse: unknown) {
  return request(
    `/api/my-account/authentication-methods/${encodeURIComponent(id)}/verify`,
    jsonBody("POST", { auth_session: authSession, authn_response: authnResponse })
  );
}

/** step-up（パスワードと MFA の再入力）の後に戻る URL を付ける */
export function stepUpUrl(base: string, returnTo: string): string {
  return `${base}?return_to=${encodeURIComponent(returnTo)}`;
}
